import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Box } from '@mui/material'
import { getSecurityKey } from '../../util/security'
import { useDispatch, useSelector } from 'react-redux'
import { selectExpandedRows, selectOptionChainTimeInterval, selectPrimarySecurity } from '../../selectors/home'
import { optionsTradesDataType } from '../typings/options.typings'
import { selectUnderlyingOptionsExpiries, selectUnderlyingOptionsTradeData } from '../../selectors/options'
import { findClosestSubarray } from '../../util/options'
import { WebSocketContext } from '../../components/webSocketConnection/WebSocket'
import { setCurentOrderPricesFromData } from '../../util/tradepanel'
import { SearchParamsContext } from '../../components/providers/searchParamsProvider'
import { selectOptionChainTableView, selectOptionChainVisibleStrikes } from '../../selectors/common'
import OptionChainTable from './optionChainViewTabs/optionChainTable'
import { setOptionChainExpandedRows } from '../../reducer/home.reducer'
import { CandleChangeManagerProvider } from '../../components/candleChangeManager/CandleChangeManagerContextprovider'
import { CandleChange } from '../../components/candleChangeManager/candleChangeManager'
import { throttle } from 'throttle-debounce'
import { updateCandleChangeBook } from '../../reducer/security.reducer'
import { selectExchangeTradingSessions, selectTradingCalendarDataByExchange } from '../../selectors/chart'
import { resolveLiveTradingSessionsForSecurity } from '../../util/tradingSessionSelection'
import { isOptionChainLiveView, OptionChainViewKey } from '../../assets/constants'
import { Security } from '../../proto/generated/security'
import { useCombineBasketAnalyzeAndLegOrders } from '../../hooks/useCombineBasketAnalyzeAndLegOrders'
import { OptionChainTableView } from './fnoHeader/optionsChainSettingsDialog'
import { resetStrategyBuilder } from '../../reducer/strategyBuilder.reducer'
import useOptionsTradeData from '../../hooks/useOptionsTradeData'
import { clearOptionChainLiveDisplayCache } from './optionChainViewTabs/components/optionChainLiveDisplay'

type Props = {
    displayAllStrikes?: boolean,
    optionChainViewKey: OptionChainViewKey
    showSingleExpiry?: boolean
    selectedOptionChainTableViewProp?: OptionChainTableView
    disableRowCollapse?: boolean
    optionsTradeDataType?: 'extended' | 'normal'
    fetchEnabled?: boolean
    /** Widget dashboard: use this security instead of global primary. */
    widgetSecurity?: Security
    /** Stable underlying key for widget dashboard fetches. */
    widgetSecurityKey?: string
}

const OptionChainTab: React.FC<Props> = ({
    displayAllStrikes,
    optionChainViewKey,
    showSingleExpiry = false,
    selectedOptionChainTableViewProp = OptionChainTableView.NORMAL,
    disableRowCollapse,
    optionsTradeDataType = 'normal',
    fetchEnabled = true,
    widgetSecurity,
    widgetSecurityKey = '',
}: Props) => {
    useOptionsTradeData({
        type: optionsTradeDataType,
        open: widgetSecurity ? true : fetchEnabled,
        optionChainViewKey,
        widgetSecurity,
        widgetSecurityKey,
    })
    const dispatch = useDispatch()
     const tableView = selectedOptionChainTableViewProp ?? useSelector(selectOptionChainTableView)
    const ws = useContext(WebSocketContext)
    const { searchParams, setSearchParams } = useContext(SearchParamsContext)!
    const { primary, underlying } = useSelector(selectPrimarySecurity)
    const security = widgetSecurity ?? underlying ?? primary
    const underlyingToken = useMemo(() => security ? getSecurityKey(security.exchange, security.code) : '', [security])
    const strikesCount: string = useSelector(selectOptionChainVisibleStrikes)
    const visibleStrikes: string = useMemo(() => (displayAllStrikes ? 'all' : strikesCount), [strikesCount, displayAllStrikes])
    const expandedRows = useSelector(selectExpandedRows(optionChainViewKey))
    const optionsTradesData = useSelector(selectUnderlyingOptionsTradeData(underlyingToken))
    const optionsExpiries = useSelector(selectUnderlyingOptionsExpiries(underlyingToken))
    const [filteredOptionsTradesData, setFilteredOptionsTradesData] = useState<optionsTradesDataType[]>([])
    const { orders, allOrders} = useCombineBasketAnalyzeAndLegOrders()
    const previousState = useRef<string>('')
    const tradeDataRef = useRef<optionsTradesDataType[]>([])
    const timeinterval = useSelector(selectOptionChainTimeInterval(optionChainViewKey, showSingleExpiry ? 'extended' : 'normal'))
    const exchangeTradingSessions = useSelector(selectExchangeTradingSessions)
    const tradingCalendarData = useSelector(selectTradingCalendarDataByExchange(security?.exchange))
    const tradingSessions = useMemo(() => {
        if (!security?.exchange || !exchangeTradingSessions) return []
        return resolveLiveTradingSessionsForSecurity(
            exchangeTradingSessions,
            tradingCalendarData,
            security
        )
    }, [
        security?.exchange,
        security?.segment,
        security?.partOfCAS,
        exchangeTradingSessions,
        tradingCalendarData
    ])

    const previousUnderlyingToken = useRef<string>(null);
    useEffect(() => {
        const prevToken = previousUnderlyingToken.current;
        if (prevToken && underlyingToken !== prevToken) {
            dispatch(resetStrategyBuilder())
        }
        previousUnderlyingToken.current = underlyingToken;
    }, [underlyingToken])


    useEffect(() => {
        if (optionsTradesData) tradeDataRef.current = optionsTradesData
    }, [optionsTradesData])

    const onCandleChangeCallback = useCallback(throttle(1000, (candleChange: Record<string, CandleChange>) => {
        dispatch(updateCandleChangeBook(Object.values(candleChange)))
    }), [dispatch])

    useEffect(() => {
        return () => {
            clearOptionChainLiveDisplayCache()
        }
    }, [])

    useEffect(() => {
        if (optionChainViewKey === OptionChainViewKey.WIDGET) return
        if (orders.length && optionsTradesData) {
            const indexes: string[] = []
            orders.forEach((o) => {
                const ind = optionsTradesData.findIndex((oe) => oe.expiration.expiry?.expiry === o.expiry)
                if (ind !== -1 && !indexes.includes(String(ind))) {
                    indexes.push(String(ind))
                }
            })
            if (indexes.length) {
                const mergedArray = [...new Set([...indexes, ...(expandedRows ?? [])])];
                dispatch(setOptionChainExpandedRows({
                    expandedRows: mergedArray,
                    optionChainViewKey: optionChainViewKey
                }))
            } else if (!indexes.length && !expandedRows.length) {
                dispatch(setOptionChainExpandedRows({
                    expandedRows: ['0'],
                    optionChainViewKey: optionChainViewKey
                }))
            }
        }
    }, [orders])

    // fetch option chain
    useEffect(() => {
        (async () => {
            const newExpandedRows = [...expandedRows]
            const state = `${newExpandedRows.sort().join()}${underlyingToken}${JSON.stringify(optionsExpiries)}`
            if (state !== previousState.current && optionsTradesData?.length) {
                previousState.current = state
                tradeDataRef.current = []
                if (allOrders && allOrders.length > 0) {
                    setCurentOrderPricesFromData(allOrders, dispatch, optionsTradesData, underlyingToken, [])
                }
            }
        })()
    }, [expandedRows, security, underlyingToken, optionsTradesData])

    useEffect(() => {
        const newData: optionsTradesDataType[] = JSON.parse(JSON.stringify(optionsTradesData ?? []))

        if (visibleStrikes !== 'all') {
            newData.forEach((d) => {
                if (Number(visibleStrikes) < d.trades.length)
                    d.trades = findClosestSubarray(d.trades, d.atmStrike, Number(visibleStrikes))
            })
        } else {
            newData.forEach((d) => {
                d.trades = d.trades.filter((trade) => ((trade.callOption?.openInterest ?? 0) > 0 && (trade.putOption?.openInterest ?? 0) > 0))
            })
        }
        if (JSON.stringify(newData) !== JSON.stringify(filteredOptionsTradesData)) setFilteredOptionsTradesData(newData)
    }, [visibleStrikes, optionsTradesData])

    useEffect(() => {
        if (optionChainViewKey === OptionChainViewKey.WIDGET) return
        searchParams.delete('strikes')
        setSearchParams(searchParams, { replace: true })
    }, [])

    const wsEnabled = isOptionChainLiveView(optionChainViewKey) && (widgetSecurity ? true : fetchEnabled)

    return (
        <CandleChangeManagerProvider
            dispatch={dispatch}
            ws={wsEnabled ? ws : null}
            subscriptionTag="option-chain-tab"
            timebucket={timeinterval}
            onCandleChangeCallback={onCandleChangeCallback}
            tradingSessions={tradingSessions}
        >
            <Box sx={{ height: '100%', width: '100%', minHeight: 0 }}>
                <OptionChainTable
                    optionChainViewKey={optionChainViewKey}
                    showSingleExpiry={showSingleExpiry}
                    selectedOptionChainTableViewProp={tableView}
                    disableRowCollapse={disableRowCollapse}
                    underlyingToken={widgetSecurityKey || underlyingToken}
                    underlyingSecurity={security}
                />
            </Box>
        </CandleChangeManagerProvider>
    )
}

export default memo(OptionChainTab)